import React, { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  CASE_STUDY_BRAND,
  industryCaseStudies,
} from '../data/industryCaseStudies';
import {
  MarketingNav,
  MarketingFooter,
  FloatingOrbs,
  fadeUp,
  AnimatedSection,
} from './IndustryCaseStudy';
import '../styles/landing-clean.css';

const CaseStudies = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <div className="landing-clean min-h-screen overflow-x-hidden" data-testid="case-studies-hub">
      <MarketingNav />

      <section
        ref={heroRef}
        className="relative min-h-[78vh] flex items-center justify-center pt-28 pb-16 px-6 overflow-hidden bg-[#fbbf0f]"
      >
        <FloatingOrbs />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(rgba(28,25,23,0.04) 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }}
        />
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 text-center max-w-4xl mx-auto">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="font-heading text-sm md:text-base font-extrabold tracking-tight text-[#16130d] mb-4"
          >
            {CASE_STUDY_BRAND}
          </motion.p>
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-[#16130d] mb-5"
          >
            Case studies by industry
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
            className="text-lg md:text-xl text-[#3a3327] font-medium max-w-2xl mx-auto mb-9 leading-relaxed"
          >
            See how restaurants, retailers, agencies, and local teams use one AI workspace to launch faster and grow smarter.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Button
                onClick={() => navigate('/auth')}
                className="!bg-white !text-[#16130d] hover:!bg-white border-2 border-[#16130d] rounded-full px-8 py-3 text-base font-bold shadow-[0_5px_0_0_#16130d] hover:shadow-[0_3px_0_0_#16130d] hover:translate-y-[2px] transition-all"
              >
                Get started free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          {industryCaseStudies.map((study, i) => (
            <AnimatedSection key={study.slug}>
              <motion.div variants={fadeUp} custom={i % 4} whileHover={{ y: -8 }} className="h-full">
                <Link
                  to={`/case-studies/${study.slug}`}
                  className="group flex flex-col h-full rounded-[1.75rem] border-2 border-[#16130d]/10 bg-[#fffefb] overflow-hidden hover:border-[#16130d]/35 transition-colors shadow-[0_10px_0_0_rgba(22,19,13,0.05)]"
                >
                  <div className="bg-[#fbbf0f] px-7 pt-7 pb-6 border-b-2 border-[#16130d]/10 relative overflow-hidden">
                    <motion.div
                      className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-white/25 blur-2xl"
                      animate={{ scale: [1, 1.15, 1] }}
                      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <p className="relative text-xs font-bold uppercase tracking-[0.16em] text-[#16130d]/70 mb-2">
                      {study.eyebrow}
                    </p>
                    <h2 className="relative font-heading text-2xl md:text-3xl font-extrabold text-[#16130d]">
                      {study.industry}
                    </h2>
                    <p className="relative mt-3 font-heading text-3xl font-extrabold text-[#16130d]">
                      {study.metricHighlight.value}
                      <span className="block text-sm font-bold text-[#3a3327] mt-1">
                        {study.metricHighlight.label}
                      </span>
                    </p>
                  </div>
                  <div className="px-7 py-6 flex flex-col flex-1">
                    <p className="text-[#5c5245] font-medium leading-relaxed flex-1">{study.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#16130d] group-hover:gap-3 transition-all">
                      Read case study <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      <section className="relative py-24 px-6 md:px-12 overflow-hidden bg-[#fbbf0f]">
        <FloatingOrbs />
        <AnimatedSection className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.h2 variants={fadeUp} custom={0} className="font-heading text-3xl md:text-5xl font-extrabold text-[#16130d] mb-4">
            Your industry next
          </motion.h2>
          <motion.p variants={fadeUp} custom={1} className="text-[#3a3327] text-lg mb-8 font-medium">
            Build the same system these teams use — websites, marketing, AI tools, and automations in one place.
          </motion.p>
          <motion.div variants={fadeUp} custom={2}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Button
                onClick={() => navigate('/auth')}
                className="!bg-white !text-[#16130d] hover:!bg-white border-2 border-[#16130d] rounded-full px-10 py-4 text-lg font-bold shadow-[0_5px_0_0_#16130d]"
              >
                Get started free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </motion.div>
        </AnimatedSection>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default CaseStudies;
